import type { AuthTokens } from '@classgo/shared/auth';

import type { ActAsHandover, AuthState, AuthStore, AuthStoreOptions, SignOutReason } from './types';

const STORAGE_KEY = 'classgo.session';
const REFRESH_MARGIN_MS = 60_000;
const MIN_DELAY_MS = 5_000;
const NETWORK_RETRY_MS = 30_000;

type Stored =
  | { kind: 'refresh'; email: string; refreshToken: string; tokens: AuthTokens | null }
  | { kind: 'actAs'; handover: ActAsHandover };

const readStored = (storage: AuthStoreOptions['storage']): Stored | null => {
  try {
    const raw = storage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    if (typeof parsed['email'] !== 'string') return null;
    if (typeof parsed['refreshToken'] === 'string') {
      const tokens =
        typeof parsed['idToken'] === 'string' &&
        typeof parsed['accessToken'] === 'string' &&
        typeof parsed['expiresAt'] === 'number'
          ? {
              idToken: parsed['idToken'],
              accessToken: parsed['accessToken'],
              refreshToken: parsed['refreshToken'],
              expiresAt: parsed['expiresAt'],
            }
          : null;
      return {
        kind: 'refresh',
        email: parsed['email'],
        refreshToken: parsed['refreshToken'],
        tokens,
      };
    }
    const actAs = parsed['actAs'] as Record<string, unknown> | undefined;
    if (
      typeof parsed['idToken'] === 'string' &&
      typeof parsed['expiresAt'] === 'number' &&
      typeof actAs?.['sessionId'] === 'string' &&
      typeof actAs['tenantId'] === 'string'
    ) {
      return {
        kind: 'actAs',
        handover: {
          email: parsed['email'],
          idToken: parsed['idToken'],
          expiresAt: parsed['expiresAt'],
          actAs: { sessionId: actAs['sessionId'], tenantId: actAs['tenantId'] },
        },
      };
    }
    return null;
  } catch {
    return null;
  }
};

/**
 * A refresh that failed because Cognito could not be reached (the wrapper maps it to the stable
 * `NETWORK` code), as opposed to one Cognito refused.
 */
const isNetworkFailure = (error: unknown): boolean =>
  typeof error === 'object' &&
  error !== null &&
  'code' in error &&
  (error as { code: unknown }).code === 'NETWORK';

/**
 * Session state outside React: the ID and access tokens live in memory only; the refresh token
 * and the email survive a reload in `sessionStorage` (per tab) so a reload silently refreshes
 * instead of asking for the password. A refresh is scheduled a minute before the ID token
 * expires; when Cognito refuses it, the session ends and the router sends the person to login;
 * when Cognito could not be reached, it is tried again every half minute while the token is
 * still valid, so a phone without signal keeps its session (ticket #119). With `persistTokens`
 * (the Expo app, whose storage is the secure store) the tokens are kept too, and a launch
 * without network opens with a token that is still valid. An act-as handover (ClassGO staff
 * inside a school) is a session without refresh: it ends at its expiry.
 *
 * @param {AuthStoreOptions} options -- Backend, storage and injectable timers.
 * @returns The store.
 */
export const createAuthStore = ({
  backend,
  storage,
  persistTokens = false,
  now = () => Date.now(),
  schedule = (callback, ms) => setTimeout(callback, ms),
  cancel = (handle) => clearTimeout(handle as ReturnType<typeof setTimeout>),
}: AuthStoreOptions): AuthStore => {
  const EMPTY: Omit<AuthState, 'status' | 'endedBy'> = { email: null, tokens: null, actAs: null };
  let state: AuthState = { status: 'booting', ...EMPTY, endedBy: null };
  let timer: unknown = null;
  const listeners = new Set<() => void>();

  const set = (next: AuthState) => {
    state = next;
    for (const listener of listeners) listener();
  };

  const clearTimer = () => {
    if (timer !== null) {
      cancel(timer);
      timer = null;
    }
  };

  const signOutLocal = (reason: SignOutReason = 'expired') => {
    clearTimer();
    storage.removeItem(STORAGE_KEY);
    backend.signOutLocal();
    set({ status: 'anonymous', ...EMPTY, endedBy: reason });
  };

  const refreshAfter = (email: string, tokens: AuthTokens, delay: number) => {
    clearTimer();
    timer = schedule(() => {
      timer = null;
      void backend
        .refresh(email, tokens.refreshToken)
        .then((fresh) => apply(email, fresh))
        .catch((error: unknown) => {
          const remaining = tokens.expiresAt * 1000 - now();
          if (isNetworkFailure(error) && remaining > 0) {
            refreshAfter(
              email,
              tokens,
              Math.min(NETWORK_RETRY_MS, Math.max(MIN_DELAY_MS, remaining)),
            );
          } else {
            signOutLocal();
          }
        });
    }, delay);
  };

  const scheduleRefresh = (email: string, tokens: AuthTokens) =>
    refreshAfter(
      email,
      tokens,
      Math.max(MIN_DELAY_MS, tokens.expiresAt * 1000 - now() - REFRESH_MARGIN_MS),
    );

  const apply = (email: string, tokens: AuthTokens) => {
    storage.setItem(
      STORAGE_KEY,
      JSON.stringify(
        persistTokens
          ? {
              email,
              refreshToken: tokens.refreshToken,
              idToken: tokens.idToken,
              accessToken: tokens.accessToken,
              expiresAt: tokens.expiresAt,
            }
          : { email, refreshToken: tokens.refreshToken },
      ),
    );
    set({ status: 'authenticated', email, tokens, actAs: null, endedBy: null });
    scheduleRefresh(email, tokens);
  };

  const applyActAs = (handover: ActAsHandover) => {
    clearTimer();
    storage.setItem(STORAGE_KEY, JSON.stringify(handover));
    set({
      status: 'authenticated',
      email: handover.email,
      tokens: {
        idToken: handover.idToken,
        accessToken: '',
        refreshToken: '',
        expiresAt: handover.expiresAt,
      },
      actAs: handover.actAs,
      endedBy: null,
    });
    timer = schedule(
      () => {
        timer = null;
        signOutLocal('expired');
      },
      Math.max(MIN_DELAY_MS, handover.expiresAt * 1000 - now()),
    );
  };

  const stored = readStored(storage);
  const anonymousBoot = () =>
    Promise.resolve().then(() => {
      storage.removeItem(STORAGE_KEY);
      set({ status: 'anonymous', ...EMPTY, endedBy: null });
      return 'anonymous' as const;
    });
  const refreshBoot = (email: string, refreshToken: string) =>
    backend
      .refresh(email, refreshToken)
      .then((tokens) => {
        apply(email, tokens);
        return 'authenticated' as const;
      })
      .catch(() => {
        signOutLocal();
        return 'anonymous' as const;
      });
  const boot: Promise<AuthState['status']> =
    stored?.kind === 'refresh'
      ? persistTokens && stored.tokens && stored.tokens.expiresAt * 1000 - now() > MIN_DELAY_MS
        ? // A token that is still valid opens the app at once, network or not; the refresh
          // keeps its usual schedule.
          Promise.resolve().then(() => {
            apply(stored.email, stored.tokens as AuthTokens);
            return 'authenticated' as const;
          })
        : refreshBoot(stored.email, stored.refreshToken)
      : stored?.kind === 'actAs' && stored.handover.expiresAt * 1000 > now()
        ? Promise.resolve().then(() => {
            applyActAs(stored.handover);
            return 'authenticated' as const;
          })
        : anonymousBoot();

  return {
    getSnapshot: () => state,
    subscribe: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    ready: () => boot,
    completeSignIn: apply,
    completeActAs: applyActAs,
    signOutLocal,
    getIdToken: () => state.tokens?.idToken ?? null,
    getActAsSession: () => state.actAs?.sessionId ?? null,
  };
};
